<template>
    <tailwind-layout title="My Rental Application" :sub-title="subTitle">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto mt-1">
                <h1 class="text-lg text-left font-bold leading-7 text-gray-700 sm:text-3xl sm:truncate">
                    {{ application ? 'My Rental Application' : 'Self Credit Check' }} {{ subTitle }}
                </h1>
                <div v-if="showHeader" class="flex gap-4 mt-4">
                    <div class="flex flex-col bg-white p-4 rounded-lg shadow-md flex-1">
                        <!-- Progress Bar -->
                        <progress-bar 
                            :title="'Application Progress'"
                            :application="application" 
                            :step-completion-status="step_completion_status"
                            :step-progress-data="step_progress_data"
                            :completed-steps-map="completed_steps_map"
                        />
                    </div>
                </div>
            </div>
        </template>
        
        <div class="relative">
            <!-- Experian Attempts Alert -->
            <div v-if="showExperianAttempts && experian_attempt && experian_attempt.attempts < 3" class="mb-6">
                <alert-card :type="'warning'">
                    <div class="">
                        <h3 class="text-sm font-medium text-yellow-800">{{experian_attempt.attempts}} Attempts Remaining</h3>
                        <div v-if="experian_attempt.attempts > 0" class="mt-2 text-sm text-yellow-700">
                            You have {{experian_attempt.attempts}} remaining attempts for to generate credit report within the next 24 hours. <button type="button" @click="showExperianHelp = true" class="text-blue-600 underline">Having Trouble? Get Tips</button>
                        </div>
                        <div v-else class="mt-2 text-sm text-yellow-700">
                            You are currently locked out due to multiple unsuccessful credit report attempts. You will be able to try again after {{experian_attempt.remaining_lockout_time}}. <button type="button" @click="showExperianHelp = true" class="text-blue-600 underline">Having Trouble? Get Tips</button>
                        </div>
                    </div>
                </alert-card>
            </div>

            <!-- Main Layout: Sidebar + Content -->
            <div class="flex gap-4 mt-6 min-w-0">
                <!-- Navigation Sidebar -->
                <div v-if="showNavigationMenu" class="flex-shrink-0 sticky top-20 self-start hidden lg:flex">
                    <navigation-new
                        :property="property"    
                        :application="application"
                        :step-completion-status="$page.props.step_completion_status"
                        :step-progress-data="$page.props.step_progress_data"
                        :completed-steps-map="$page.props.completed_steps_map"
                        :current-step="currentStep"
                        @navigate="handleNavigation"
                    />
                </div>
                
                <!-- Content Area -->
                <div class="flex-1 min-w-0">
                    <slot />
                </div>
            </div>
        </div>

        <!-- Modals -->
        <experian-info-popup v-model:open="showExperianHelp" />
        
        <modal :open="isSharedWarningModalOpen" @closeAlert="isSharedWarningModalOpen = false" class="text-center">
            <div as="h3" class="text-xl font-semibold leading-6 text-gray-900 flex items-center justify-center gap-x-2 mb-5">
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                    <font-awesome-icon :icon="faExclamation" class="h-6 w-6 text-red-500" aria-hidden="true" />
                </div>
                <span>Attention</span>
            </div>

            <div v-if="application.is_credit_report_recently_generated && application.is_credit_report_recently_generated === application.id" class="mt-2 text-left">
                <p class="text-sm">Your application is NOT complete until you click the SHARE button at the top of the credit report.</p>
                <p class="text-sm mt-2">Next, you will need to upload photo ID and proof of income documents.</p>
            </div>
            <div v-else class="mt-2 text-left">
                <p class="text-sm">You have not shared your credit report with the agent or landlord.</p>
                <p class="text-sm mt-2">Your application is NOT COMPLETE until you click the SHARE button at the top of the credit report.</p>
            </div>
        </modal>
        
        <full-page-loader v-if="isLoader"></full-page-loader>
    </tailwind-layout>
</template>

<script>import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faExclamation } from '@fortawesome/pro-light-svg-icons'

import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import AlertCard from "@/Shared/AlertCard.vue";
import Modal from "@/Shared/Modal.vue";
import { router } from '@inertiajs/vue3';
import FullPageLoader from "../../Shared/FullPageLoader.vue";
import ExperianInfoPopup from '../../Shared/ExperianInfoPopup.vue';
import ProgressBar from './ProgressBar.vue';
import NavigationNew from "@/Pages/Applications/NavigationNew.vue";

export default {
    components: {
        TailwindLayout,
        AlertCard,
        Modal,
        ExperianInfoPopup,
        ProgressBar,
        NavigationNew,
        FullPageLoader,
    FontAwesomeIcon
  },
    props: {
        property: {
            type: Object,
            required: false,
            default: null
        },
        application: {
            type: Object,
            required: false,
            default: null
        },
        experian_attempt: {
            type: Object,
            default: () => ({})
        },
        step_completion_status: {
            type: Object,
            default: () => ({})
        },
        step_progress_data: {
            type: Object,
            default: () => ({})
        },
        completed_steps_map: {
            type: Object,
            default: () => ({})
        },
        currentStep: {
            type: String,
            default: 'dashboard'
        },
        subTitle: {
            type: String,
            default: ''
        },
        showNavigationMenu: {
            type: Boolean,
            default: true
        },
        showHeader: {
            type: Boolean,
            default: true
        },
        showExperianAttempts: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            isLoader: false,
            isSharedWarningModalOpen: false,
            showExperianHelp: false,
            hasShownWarning: false, // Track if we've already shown the warning
        }
    },
    mounted() {
        // Only show the modal once per session and only on dashboard
        if (this.application && this.application.is_show_report_not_shared_warning 
            && this.$page.component === 'Applications/Dashboard'
            && !this.hasShownWarning
            && !sessionStorage.getItem(`warning_shown_${this.application.id}`)) {
            this.isSharedWarningModalOpen = true;
            this.hasShownWarning = true;
            // Store in session storage to prevent showing again during this session
            sessionStorage.setItem(`warning_shown_${this.application.id}`, 'true');
        }
    },
    methods: {
        handleNavigation(stepName) {
            // Map step names to routes
            const routeMap = {
                'terms_and_conditions': 'application.terms-and-conditions',
                'personal_information': 'application.personal-information',
                'credit_background_report': 'application.credit-report',
                'financial_documents': 'application.financial-documents',
                'share_report': 'application.share'
            };

            const routeName = routeMap[stepName];
            if (routeName) {
                router.visit(route(routeName, this.application.id), {
                    only: ['currentStep'], // Only reload specific props
                    preserveState: true,
                    preserveScroll: true
                });
            } else {
                // Default to dashboard
                router.visit(route('application.steps', this.application.id), {
                    only: ['currentStep'],
                    preserveState: true,
                    preserveScroll: true
                });
            }
        }
    }
}
</script>